import { useState, useEffect } from 'react'
import { useLazyQuery } from '@apollo/client'
import { GET_CONTENT_TRANSCRIPT } from '../queries'
import type { ContentTranscriptData } from './ai-types'

/**
 * Custom hook for fetching video transcripts
 * Handles GraphQL query execution, transcript extraction, and error handling
 *
 * @returns Object containing fetchTranscript function, transcript text, loading state, and error
 */
export const useTranscriptFetcher = () => {
  const [transcript, setTranscript] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  // Apollo lazy query for fetching transcript
  const [fetchTranscriptQuery, { data: transcriptData, error: transcriptError }] =
    useLazyQuery<ContentTranscriptData>(GET_CONTENT_TRANSCRIPT, {
      fetchPolicy: 'network-only',
    })

  // Handle transcript query results with useEffect
  useEffect(() => {
    if (!transcriptData && !transcriptError) return

    const processTranscript = () => {
      if (transcriptError) {
        setError(`Failed to fetch transcript: ${transcriptError.message}`)
        setLoading(false)
        return
      }

      try {
        // Extract transcript from featureMedia.mediaAssets.aiMetadata
        const contentItem = transcriptData?.contentList?.content?.[0]
        const mediaAssets = contentItem?.featureMedia?.mediaAssets || []

        if (mediaAssets.length === 0) {
          setError(
            'No media assets found for this video. Please ensure the video has feature media attached.',
          )
          setLoading(false)
          return
        }

        // Find the first media asset with a transcript
        const assetWithTranscript = mediaAssets.find((asset) => asset?.aiMetadata?.transcript)

        if (!assetWithTranscript?.aiMetadata?.transcript) {
          setError(
            'No transcript available for this video. Please ensure the video has been processed with AI metadata.',
          )
          setLoading(false)
          return
        }

        const transcriptText = assetWithTranscript.aiMetadata.transcript

        if (!transcriptText.trim()) {
          setError('Transcript is empty. Please try again or contact support.')
          setLoading(false)
          return
        }

        setTranscript(transcriptText)
        setError(null)
        setLoading(false)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error'
        setError(`Failed to process transcript: ${errorMessage}`)
        setLoading(false)
      }
    }

    processTranscript()
  }, [transcriptData, transcriptError])

  /**
   * Fetch transcript for a given content ID
   * @param contentId - The content ID to fetch transcript for
   */
  const fetchTranscript = (contentId: number | string): void => {
    // Clear previous errors and data
    setError(null)
    setTranscript(null)
    setLoading(true)

    // Fetch the transcript
    fetchTranscriptQuery({
      variables: {
        contentId: typeof contentId === 'number' ? contentId : parseInt(contentId, 10),
      },
    })
  }

  return {
    fetchTranscript,
    transcript,
    loading,
    error,
  }
}
